import { Alert, Autocomplete, CircularProgress, Snackbar, TextField } from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { Required } from "ui-component/custom-components/mandatory";

const VWDropdown = (props) => {
    const { label, name, multiple, limitTags, onSelect, selectedOption, getOptions, setOptions, options,
        isOptionEqualToValue, autoFocus, disablePortal, error, isRequired, isDisabled, onKeyDown, selectedLimit } = props;
    const { multiFilterOptionsLimit } = window.__ENV || {};

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [typedValue, setTypedValue] = useState("");
    const [hasMore, setHasMore] = useState(true);
    const [triggerSnackbar, setTriggerSnackbar] = useState(false);

    const PAGE_SIZE = 50;
    const skipRef = useRef(0);
    //-------------------------------Get Options--------------------------------
    const loadOptions = (filter) => {
        setIsLoading(true);
        getOptions(filter, skipRef.current, PAGE_SIZE)
            .then((result) => {
                setOptions(result);
                setIsLoading(false);
            })
            .catch(() => {
                setOptions([]);
                setIsLoading(false);
            })
    }

    const loadOptionsScroll = (filter) => {
        if (isLoading || !hasMore) return;
        setIsLoading(true);
        getOptions(filter, skipRef.current, PAGE_SIZE)
            .then((result) => {
                setOptions(result);
                setIsLoading(false);
                skipRef.current += PAGE_SIZE;
                if (result.length < PAGE_SIZE) {
                    setHasMore(false);
                }
            })
            .catch(() => {
                setOptions([]);
                setIsLoading(false);
            })
    }


    const handleScroll = (event) => {
        const listboxNode = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = listboxNode;

        if (scrollTop + clientHeight >= scrollHeight - 5) {
            loadOptionsScroll();
        }
    };

    //-------------------------------Menu Open----------------------------------
    const handleOpen = () => {
        setIsOpen(true);
        setOptions([], "clear");
        skipRef.current = 0;
        if (getOptions) {
            loadOptions();
        }
    }

    //-------------------------------Menu Close---------------------------------
    const handleClose = () => {
        setIsOpen(false);
        setOptions([], "clear");
    }

    //-------------------------------Options Filter-----------------------------
    const debouncedFetchOptions = useMemo(() => debounce((query) => loadOptions(query), 500), [getOptions]);
    const handleFilter = (e, newValue, reason) => {
        const { value } = e?.target || {};
        if (reason === "input") {
            setTypedValue(newValue);
            debouncedFetchOptions(value);
        }
    };

    //-------------------------------Options Select-----------------------------
    const handleSelect = (e, option, isNew, details) => {
        setTypedValue("");
        if ((Array.isArray(option) && option?.length > multiFilterOptionsLimit) && (selectedLimit !== "skip")) {
            setTriggerSnackbar(true)
        }
        else {
            onSelect(option, name, isNew, details);
        }
    }

    //-------------------------------Render Text Field--------------------------
    const getTextField = (params) => {
        return (
            <TextField
                {...params}
                label={isRequired ? <div >{label} <Required /></div> : label}
                autoFocus={autoFocus}
                error={error}
                helperText={error}
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                        <>
                            {isLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                        </>
                    ),
                }}
            />
        )
    }

    //-------------------------------Render Options-----------------------------
    const getRenderOptions = (props, option) => {
        return (
            <li {...props} key={option?.value}>
                {option?.label}
            </li>
        )
    }

    return (
        <div style={{ position: "relative" }}>
            {multiple ?
                <Autocomplete
                    size="small"
                    disablePortal={disablePortal}
                    multiple={multiple}
                    limitTags={limitTags}
                    filterSelectedOptions
                    isOptionEqualToValue={isOptionEqualToValue}
                    options={options}
                    open={isOpen}
                    loading={isLoading}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onChange={handleSelect}
                    value={selectedOption}
                    onInputChange={handleFilter}
                    inputValue={typedValue}
                    onBlur={() => setTypedValue("")}
                    renderInput={getTextField}
                    renderOption={getRenderOptions}
                    disabled={isDisabled}
                    onKeyDown={onKeyDown}
                    ListboxProps={{
                        onScroll: handleScroll,
                        style: { maxHeight: 300, overflow: "auto" },
                    }}
                />
                :
                <Autocomplete
                    size="small"
                    disablePortal={disablePortal}
                    filterSelectedOptions
                    isOptionEqualToValue={isOptionEqualToValue}
                    options={options}
                    open={isOpen}
                    loading={isLoading}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onChange={handleSelect}
                    value={selectedOption}
                    onInputChange={handleFilter}
                    renderInput={getTextField}
                    renderOption={getRenderOptions}
                    disabled={isDisabled}
                    onKeyDown={onKeyDown}
                    ListboxProps={{
                        onScroll: handleScroll,
                        style: { maxHeight: 300, overflow: "auto" },
                    }}
                />
            }
            <Snackbar
                open={triggerSnackbar}
                autoHideDuration={3000}
                onClose={() => setTriggerSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{
                    position: 'absolute',
                    top: '100%',
                    mt: 4,
                }}
            >
                <Alert
                    variant="filled"
                    sx={{ fontSize: 13, backgroundColor: "#fc6464ff", color: "white" }}
                >
                    You can only select up to {multiFilterOptionsLimit} options
                </Alert>
            </Snackbar>
        </div>
    )
}

export default VWDropdown;